import { FragilePopup, PopupPosition } from "./dialogs";
import { TooltipView } from "@fullstory/tomato-views/views";
import { Q } from '@fullstory/tomato';

export enum ToolTipColor {
  Gray = 1,
  Pink,
  Red,
  Yellow,
  Green,
  Blue,
  Black,
  White
}

export enum ToolTipEvent {
  Click = 1,
  Hover
}

export interface ToolTipOptions {
  Color?: ToolTipColor,
  Position?: number,
  Event?: ToolTipEvent
}

export class ToolTip extends FragilePopup<TooltipView> {
  private _pointerIndent = 24; // The amount that we indent the pointer on the tooltip from the edge of the tip.

  constructor(trigger: Q.OfOne, contents: Q.OfOne, options?: ToolTipOptions, private _margin: { x: number, y: number } = { x: 0, y: 0 }) {
    super(new TooltipView());
    this.view().append(contents);
    this.setPadding(15, 15);
    this.setColor(options ? options.Color : ToolTipColor.Gray);
    // Default position elem.
    this.positionElem = trigger;
    let eventType = options && options.Event ? options.Event : ToolTipEvent.Click;
    if (options && options.Position) {
      this.triggeredBy(trigger, eventType, options.Position).appendTo(Q.body());
    } else {
      this.triggeredBy(trigger, eventType).appendTo(Q.body());
    }
  }

  setPositionElem(elem: Q.OfOne): ToolTip {
    this.positionElem = elem;
    return this;
  }

  triggeredBy(trigger: Q.OfOne, eventType: ToolTipEvent, position?: number): this {
    this.triggerElem = trigger;
    if (eventType == ToolTipEvent.Click) {
      this.triggerElem.on("click", () => {
        if (!this.triggerDisabled) { this.toggle(); }
      });
    } else {
      // Assume hover.
      this.triggerElem.on("mouseenter", () => {
        if (this.isHiding()) { return; }
        if (!this.triggerDisabled) { this.show(); }
      });
      this.triggerElem.on("mouseleave", () => {
        setTimeout(() => {
          if (!this.triggerDisabled) { this.hide(); }
        }, 200);
      });

      // If we mouse over the popup itself, disable the trigger. Re-enable when leaving.
      this.view().on("mouseenter", () => {
        if (this.isHiding()) { return; }
        this.disableTrigger(true);
      });
      this.view().on("mouseleave", () => {
        this.disableTrigger(false);
        setTimeout(() => {
          if (!this.triggerDisabled) { this.hide(); }
        }, 200);
      });
    }

    if (position) {
      this.position(position, trigger);
    }
    return this;
  }

  private setColor(color?: ToolTipColor) {
    switch (color) {
      case ToolTipColor.Pink:
        this.view().addClass("pink");
        break;
      case ToolTipColor.Red:
        this.view().addClass("red");
        break;
      case ToolTipColor.Yellow:
        this.view().addClass("yellow");
        break;
      case ToolTipColor.Green:
        this.view().addClass("green");
        break;
      case ToolTipColor.Blue:
        this.view().addClass("blue");
        break;
      case ToolTipColor.Black:
        this.view().addClass("black");
        break;
      case ToolTipColor.White:
        this.view().addClass("white");
        break;
      case ToolTipColor.Gray:
      default:
        this.view().addClass("gray");
        break;
    }
  }

  // Finds a position that is within the browser window.
  // TODO(stephanie): Avoid overlapping the trigger elem if triggerElem != positionElem.
  private autoPosition(): PopupPosition {
    const positionElemRect =
      this.positionElem ? this.positionElem.bounds() : this.view().bounds();

    // get the window viewport
    const { innerWidth, innerHeight } = window;

    // calculate the element positions relative to window boundaries
    const willOverlapAbove = positionElemRect.top - (this._margin.x + this._pointerIndent) < 0;
    const willOverlapRight = positionElemRect.left + positionElemRect.width + (this._margin.y + this._pointerIndent) > innerWidth;
    const willOverlapBelow = positionElemRect.top + positionElemRect.height + (this._margin.y + this._pointerIndent) > innerHeight;
    const willOverlapLeft = positionElemRect.left - (this._margin.x + this._pointerIndent) < 0;

    let popupPosition: PopupPosition = PopupPosition.ABOVE_CENTER;

    if (willOverlapAbove && willOverlapRight && willOverlapBelow && willOverlapLeft) {
      // no way to fit this tooltip on the outside of the element
      return PopupPosition.INSIDE_LEFT;
    }

    // determine the final popup position: pick the first usable position
    if (!willOverlapAbove) {
      popupPosition = PopupPosition.ABOVE_CENTER;
    } else if (!willOverlapRight) {
      popupPosition = PopupPosition.RIGHT;
    } else if (!willOverlapBelow) {
      popupPosition = PopupPosition.MAYBE_BELOW_CENTER;
    } else if (!willOverlapLeft) {
      popupPosition = PopupPosition.LEFT;
    }

    // return the (maybe default) popup position
    return popupPosition;
  }

  protected _position(pos: number) {
    // If a position isn't set, autoposition this popup next to the trigger element.
    if (!pos || !this.positionElem) {
      pos = this.autoPosition();
      this.position(pos, this.positionElem);
    }

    const rect = this.positionElem ? this.positionElem.bounds() : this.view().bounds();
    const view = this.view();

    // TODO(jaime): Reconcile this with the positioning in dialogs.ts. This seems potentially redundant?

    // In case this is being repositioned, remove existing position-based styles.
    view.removeClass("belowright").removeClass("belowleft").removeClass("aboveright").removeClass("abovecenter")
      .removeClass("clippedleft").removeClass("aboveleft").removeClass("right").removeClass("left");

    switch (pos) {
      // Lines up the panel right edge with the positionElem right edge.
      case PopupPosition.MAYBE_BELOW_RIGHT:
        view.addClass("belowright");
        view.setCss('position', 'fixed');
        view.setCss('right', window.innerWidth - rect.right - this._pointerIndent);
        view.setCss('top', rect.bottom + this._topPad);
        break;
      case PopupPosition.ABOVE_RIGHT:
        view.addClass("aboveright");
        view.setCss('position', 'fixed');
        view.setCss('right', window.innerWidth - rect.right - this._pointerIndent);
        view.setCss('bottom', window.innerHeight - rect.top + this._topPad);
        break;
      case PopupPosition.ABOVE_CENTER:
        view.addClass("abovecenter");
        let viewBounds = view.bounds();
        let left = ((rect.right + rect.left) / 2) - (viewBounds.width / 2);
        if (left < 0) {
          left = 0;
          view.addClass("clippedleft");
        }
        view.setCss('position', 'fixed');
        view.setCss('bottom', window.innerHeight - rect.top + this._topPad);
        view.setCss('left', left);
        break;
      // Lines up the FragilePopup left edge with the positionElem left edge.
      case PopupPosition.MAYBE_BELOW_LEFT:
        view.addClass("belowleft");
        view.setCss('position', 'fixed');
        view.setCss('left', rect.left - this._pointerIndent);
        view.setCss('top', rect.bottom + this._topPad);
        break;
      case PopupPosition.ABOVE_LEFT:
        view.addClass("aboveleft");
        view.setCss('position', 'fixed');
        view.setCss('left', rect.left - this._pointerIndent);
        view.setCss('bottom', window.innerHeight - rect.top + this._topPad);
        break;
      case PopupPosition.MAYBE_BELOW_CENTER:
        view.addClass("belowcenter");
        view.setCss('position', 'fixed');
        const viewRect = view.bounds(),
          viewWidth = viewRect.right - viewRect.left;
        view.setCss('left', (rect.right + rect.left - viewWidth) / 2);
        view.setCss('top', rect.bottom + this._topPad);
        break;
      case PopupPosition.RIGHT:
        view.addClass("right");
        view.setCss('position', 'fixed');
        view.setCss('left', rect.right + this._sidePad);
        view.setCss('top', rect.top - this._pointerIndent);
        break;
      case PopupPosition.LEFT:
        view.addClass("left");
        view.setCss('position', 'fixed');

        let myWidth = view.e.offsetWidth;

        view.setCss('left', rect.left - myWidth - this._sidePad);
        view.setCss('top', rect.top - this._pointerIndent);
        break;
      case PopupPosition.INSIDE_LEFT:
        view.addClass("belowleft");
        view.setCss('position', 'fixed');
        view.setCss('left', rect.left);
        view.setCss('top', rect.top + this._pointerIndent);
        break;
    }
  }
}
